import {
  ArrowRight,
  CircleCheck,
  History,
  Minus,
  PauseCircle,
  PlayCircle,
  Plus,
  RotateCcw,
  type LucideIcon,
} from "lucide-react";

// Payroll module enums. Mirror the backend PG ENUM values exactly.
// Always send/receive raw UPPERCASE_SNAKE strings — do not lowercase or
// translate at the wire boundary.

export enum PayrollFrequencyEnum {
  MONTHLY = "MONTHLY",
  WEEKLY = "WEEKLY",
  BIWEEKLY = "BIWEEKLY",
  YEARLY = "YEARLY",
}

/**
 * How a mid-period joiner's partial month becomes a fraction of full pay.
 * CALENDAR_DAYS is the default and the behaviour payroll had before the basis
 * was configurable.
 */
export enum PayrollProrationBasisEnum {
  CALENDAR_DAYS = "CALENDAR_DAYS",
  WORKING_DAYS = "WORKING_DAYS",
  FIXED_30 = "FIXED_30",
}

export enum PaymentMethodTypeEnum {
  WPS = "WPS",
  BANK_TRANSFER = "BANK_TRANSFER",
  CHEQUE = "CHEQUE",
  CASH = "CASH",
}

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  [PaymentMethodTypeEnum.WPS]: "WPS",
  [PaymentMethodTypeEnum.BANK_TRANSFER]: "Bank Transfer",
  [PaymentMethodTypeEnum.CASH]: "Cash",
  [PaymentMethodTypeEnum.CHEQUE]: "Cheque",
};

export enum GratuityCalculationMethodEnum {
  LEGAL = "LEGAL",
  CUSTOM = "CUSTOM",
}

export enum GratuityBasisSalaryEnum {
  BASIC_ONLY = "BASIC_ONLY",
  BASIC_PLUS_ALLOWANCES = "BASIC_PLUS_ALLOWANCES",
  GROSS = "GROSS",
}

export enum EmiratisationHeadcountBandEnum {
  UNDER_20 = "UNDER_20",
  BAND_20_49 = "BAND_20_49",
  BAND_50_PLUS = "BAND_50_PLUS",
}

// Run lifecycle:
// GENERATING → DRAFT → IN_REVIEW → APPROVED → PROCESSING → COMPLETED
//                                                ↘ CANCELLED / FAILED
export enum PayrollRunStatusEnum {
  GENERATING = "GENERATING",
  DRAFT = "DRAFT",
  IN_REVIEW = "IN_REVIEW",
  APPROVED = "APPROVED",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED",
}

export enum PayrollRunScopeTypeEnum {
  BRANCH = "BRANCH",
  DEPARTMENT = "DEPARTMENT",
  PAYROLL_GROUP = "PAYROLL_GROUP",
  MIXED = "MIXED",
  EMPLOYEES = "EMPLOYEES",
}

export enum PayrollRunItemStatusEnum {
  PENDING = "PENDING",
  REVIEWED = "REVIEWED",
  APPROVED = "APPROVED",
  EXCLUDED = "EXCLUDED",
}

// FE splits EARNING/BENEFIT/DEDUCTION into 3 visible groups; STATUTORY is
// rendered separately in the employer-cost section.
export enum PayrollRunItemLineTypeEnum {
  EARNING = "EARNING",
  BENEFIT = "BENEFIT",
  DEDUCTION = "DEDUCTION",
  STATUTORY = "STATUTORY",
}

// Origin of a payroll line; used for read-only display badges.
export enum PayrollRunItemLineSourceEnum {
  COMPENSATION = "COMPENSATION",
  ADJUSTMENT = "ADJUSTMENT",
  STATUTORY = "STATUTORY",
  PRORATION = "PRORATION",
  LOSS_OF_PAY = "LOSS_OF_PAY",
  OVERTIME = "OVERTIME",
  PENALTY = "PENALTY",
}

export enum PayrollRunStatutoryTypeEnum {
  GPSSA = "GPSSA",
}

export enum PayrollRunStatutoryPartyEnum {
  EMPLOYEE = "EMPLOYEE",
  EMPLOYER = "EMPLOYER",
  GOVERNMENT = "GOVERNMENT",
}

export enum LopSourceEnum {
  AUTO = "AUTO",
  MANUAL = "MANUAL",
}

export enum PenaltyAmountTypeEnum {
  FIXED = "FIXED", // flat AED per occurrence
  PERCENT_BASIC = "PERCENT_BASIC", // % of basic salary
  DAY_RATE = "DAY_RATE", // multiplier of one day's pay
  HOURLY_RATE = "HOURLY_RATE", // multiplier of hourly rate × late/early time beyond grace
}

export enum LeavePayBasisEnum {
  BASIC_ONLY = "BASIC_ONLY",
  BASIC_PLUS_ALLOWANCES = "BASIC_PLUS_ALLOWANCES",
  GROSS = "GROSS",
}

export enum PaidStatusEnum {
  PAID = "PAID",
  NOT_PAID = "NOT_PAID",
}

// Employee-perspective payslip status (derived on the server from item state +
// run state). Keep in sync with backend EmployeePayrollStatusEnum.
export enum EmployeePayrollStatusEnum {
  PAID = "PAID",
  PROCESSED = "PROCESSED",
  APPROVED = "APPROVED",
  HOLD = "HOLD",
  EXCLUDED = "EXCLUDED",
  FAILED = "FAILED",
}

export enum PayrollAuditEntityTypeEnum {
  RUN = "RUN",
  ITEM = "ITEM",
  PAYMENT_FILE = "PAYMENT_FILE",
}

export enum PayrollAuditActionEnum {
  RUN_CREATED = "RUN_CREATED",
  RUN_UPDATED = "RUN_UPDATED",
  RUN_SUBMITTED_FOR_REVIEW = "RUN_SUBMITTED_FOR_REVIEW",
  RUN_APPROVED = "RUN_APPROVED",
  RUN_PROCESSED = "RUN_PROCESSED",
  RUN_COMPLETED = "RUN_COMPLETED",
  RUN_CANCELLED = "RUN_CANCELLED",
  RUN_RETRIED = "RUN_RETRIED",
  ITEM_UPDATED = "ITEM_UPDATED",
  ITEM_REVIEWED = "ITEM_REVIEWED",
  ITEM_HELD = "ITEM_HELD",
  ITEM_RELEASED = "ITEM_RELEASED",
  ITEM_RETRIED = "ITEM_RETRIED",
  ITEM_MARKED_PAID = "ITEM_MARKED_PAID",
  PAYMENT_FILE_GENERATED = "PAYMENT_FILE_GENERATED",
}

// ─── Save reason presets ──────────────────────────────────────────────────────

export const PAYROLL_ITEM_REASON_PRESETS: readonly string[] = [
  "Correction of incorrect data",
  "Approved manual adjustment",
  "Compliance / statutory update",
  "Late attendance correction",
  "Bonus / incentive added",
  "Deduction reversal",
];

export const PAYROLL_ITEM_REASON_MIN_LENGTH = 3;
export const PAYROLL_ITEM_REASON_MAX_LENGTH = 500;

// ─── Audit field label map ────────────────────────────────────────────────────

export const PAYROLL_AUDIT_FIELD_LABELS: Record<string, string> = {
  basicSalary: "Basic Salary",
  paymentMethod: "Payment Method",
  workingDays: "Working Days",
  paidDays: "Paid Days",
  unpaidLeaveDays: "Unpaid Leave Days",
  overtimeHours: "Overtime Hours",
  lopAmount: "LOP Amount",
  lopDays: "LOP Days",
  lopSource: "LOP Source",
  payrollCountryRuleId: "Country Rule",
  isReviewed: "Reviewed",
  status: "Status",
  bankName: "Bank Name",
  accountNumber: "Account Number",
  iban: "IBAN",
  routingNumber: "Routing Number",
  swiftCode: "SWIFT Code",
  holderName: "Account Holder",
  excludedReason: "Excluded Reason",
  holdSalary: "Salary Held",
};

// ─── Audit action metadata ────────────────────────────────────────────────────

export type PayrollAuditActionTone = "info" | "success" | "warn" | "neutral";

export interface IPayrollAuditActionMeta {
  label: string;
  tone: PayrollAuditActionTone;
  Icon: LucideIcon;
}

export const PAYROLL_AUDIT_ACTION_META: Record<PayrollAuditActionEnum, IPayrollAuditActionMeta> = {
  [PayrollAuditActionEnum.RUN_CREATED]:              { label: "Run created",            tone: "info",    Icon: Plus },
  [PayrollAuditActionEnum.RUN_UPDATED]:              { label: "Run updated",            tone: "info",    Icon: History },
  [PayrollAuditActionEnum.RUN_SUBMITTED_FOR_REVIEW]: { label: "Sent for review",        tone: "info",    Icon: ArrowRight },
  [PayrollAuditActionEnum.RUN_APPROVED]:             { label: "Run approved",           tone: "success", Icon: CircleCheck },
  [PayrollAuditActionEnum.RUN_PROCESSED]:            { label: "Run processed",          tone: "success", Icon: CircleCheck },
  [PayrollAuditActionEnum.RUN_COMPLETED]:            { label: "Run completed",          tone: "success", Icon: CircleCheck },
  [PayrollAuditActionEnum.RUN_CANCELLED]:            { label: "Run cancelled",          tone: "warn",    Icon: Minus },
  [PayrollAuditActionEnum.RUN_RETRIED]:              { label: "Run retried",            tone: "info",    Icon: RotateCcw },
  [PayrollAuditActionEnum.ITEM_UPDATED]:             { label: "Payslip modified",       tone: "info",    Icon: History },
  [PayrollAuditActionEnum.ITEM_REVIEWED]:            { label: "Marked as reviewed",     tone: "success", Icon: CircleCheck },
  [PayrollAuditActionEnum.ITEM_HELD]:                { label: "Put on hold",            tone: "warn",    Icon: PauseCircle },
  [PayrollAuditActionEnum.ITEM_RELEASED]:            { label: "Released from hold",     tone: "success", Icon: PlayCircle },
  [PayrollAuditActionEnum.ITEM_RETRIED]:             { label: "Item retried",           tone: "info",    Icon: RotateCcw },
  [PayrollAuditActionEnum.ITEM_MARKED_PAID]:         { label: "Marked as paid",         tone: "success", Icon: CircleCheck },
  [PayrollAuditActionEnum.PAYMENT_FILE_GENERATED]:   { label: "Payment file generated", tone: "success", Icon: CircleCheck },
};

export const PAYROLL_AUDIT_TONE_BADGE: Record<PayrollAuditActionTone, string> = {
  info:    "bg-info-muted text-info border-blue-200",
  success: "bg-green-50 text-green-700 border-green-200",
  warn:    "bg-amber-50 text-amber-700 border-amber-200",
  neutral: "bg-gray-50 text-gray-700 border-border",
};

// ─── Line type labels ─────────────────────────────────────────────────────────

export const PAYROLL_LINE_TYPE_LABELS: Record<string, string> = {
  EARNING: "Earning",
  BENEFIT: "Benefit",
  DEDUCTION: "Deduction",
  GRATUITY: "Gratuity",
  LEAVE_ENCASHMENT: "Leave Encashment",
};
